/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {Injectable} from '@angular/core';

import {BehaviorSubject, Observable, of} from 'rxjs';
import {map, switchMap, tap} from 'rxjs/operators';

import {PayExecLegacy} from './pay-result-detail.component';
import {PayExecType, PayResultAttributes, PayTag, RetroValue} from './pay-results.model';

import {ObserveType, REQUEST_OPTIONS, ResponseType} from '../../app.constants';
import {ApiRequestOptions, ApiService, Data} from '../../shared/api';
import {PayStatus} from '../../shared/employees/employees.model';
import {FileService} from '../../shared/files/file.service';
import {FileModel} from '../../shared/utils/files';
import {Headers} from '../../shared/utils/headers';
import {Pay, PayExecBody, RetroContractChangesData} from '../ongoing/ongoing-payrolls.interfaces';

export interface PayExecWithTotal {
  data: PayExecLegacy[];
  total: number;
}

@Injectable()
export class PayResultsService {

  public payExecLogSubject: BehaviorSubject<any[]> = new BehaviorSubject(null);

  constructor(private api: ApiService,
              private fileService: FileService) {
  }

  public getPayExecList(contractId: number, page = 0): Observable<PayExecWithTotal> {
    return this.api
      .get(`/payroll/api/v1/payExec/empContract/${contractId}?page=${page}`, REQUEST_OPTIONS.OBSERVE.RESPONSE)
      .pipe(
        map(response => {
          return {
            data: response.body.data,
            total: parseInt(Headers.get(response.headers, 'X-Total-Count'), 10)
          };
        })
      );
  }

  public getPayExecRetroChanges(pay: Pay): Observable<RetroContractChangesData> {
    return this.getPayExecList(pay.contractID, 0)
      .pipe(
        map(response => this.findPayExec(response.data, pay)),
        switchMap(payExec => payExec ? this.getPayExecRetroByDate(pay.contractID, payExec.attributes.payState.computeDate) : of(null))
      );
  }

  public getPayExecRetroByDate(contractId: number, lastExecutionDate: Date): Observable<RetroContractChangesData> {
    return this.api.get(`/ga/api/v1/empContract/${contractId}/retroactivityDate?lastExecutionDate=${lastExecutionDate}`);
  }

  public findPayExec(data: any[], pay: Pay): any {
    return data.find(exec => exec.attributes.payRollType === PayExecType.NORMAL
      && (exec.attributes.payTypeName || exec.attributes.payPeriodContractPayTypeName) === pay.payTypeName
      && exec.attributes.payState.status !== PayStatus.ERROR);
  }

  public getPayExecComparision(sourcePeriod: number,
                               targetPeriod: number,
                               sourceTypeAll: boolean,
                               targetTypeAll: boolean) {

    return this.api.get(`/payroll/api/v1/payExec/comparision`, {
      requestOptions: {
        params: {
          sourcePeriod, targetPeriod, sourceTypeAll, targetTypeAll
        }
      }
    })
      .pipe(
        map(data => data.map(item => {
          item.name = (item.name || '').replace(/\.sum$/, '');
          return item;
        }))
      );
  }

  public getPayResult(payExecId: number, currency?: string): Observable<Array<Data<PayResultAttributes>>> {
    return this.api.get(`/payroll/api/v1/payExec/${payExecId}/payResult`, {
      requestOptions: {
        params: currency ? {currency} : {},
        disableCache: false
      }
    })
      .pipe(map(result => result.data));
  }

  public getPayExecCalendar(payExecId: number): Observable<any[]> {
    return this.api.get(`/payroll/api/v1/payExec/${payExecId}/payCalendarDay`)
      .pipe(map(result => result.data));
  }

  public getPayExecLog(payExecId: number): Observable<any[]> {
    return this.api.get(`/payroll/api/v1/payExec/${payExecId}`)
      .pipe(
        tap((response) => {
          this.payExecLogSubject.next(response);
        })
      );
  }

  public getPayExecListOnOptions(opts: ApiRequestOptions): Observable<PayExecBody[]> {
    return this.api.get(`/payroll/api/v1/payExec`, opts)
      .pipe(map(response => response.body.data));
  }

  public getPayExecLogFile(logUrl: string): Observable<string> {
    return this.api.get(logUrl, {
      requestOptions: {
        observe: ObserveType.RESPONSE,
        responseType: ResponseType.TEXT
      }
    }).pipe(map((response: any) => response.body));
  }

  public getPayExecOverviewData(payExecId: number, currency: string, rateDate: string, resultTypeAll: boolean) {
    return this.api.get(`/payroll/api/v1/payExec/${payExecId}/overview`, {
      requestOptions: {
        params: {currency, rateDate, resultTypeAll}
      }
    });
  }

  public getPayslipHtml(payslipHtmlFileId: number): Observable<FileModel> {
    return this.fileService.getFile(payslipHtmlFileId);
  }

  public getRetroValue(payExecId: number): Observable<RetroValue[]> {
    return this.api.get(`/payroll/api/v1/payExec/${payExecId}/retroValue`).pipe(map(result => result.data));
  }

  public countRetroPayslips(payExecId: number): Observable<number> {
    return this.api
      .head(`/payroll/api/v1/pay-exec/${payExecId}/retro-payslips`, REQUEST_OPTIONS.OBSERVE.RESPONSE)
      .pipe(map(result => parseInt(Headers.get(result.headers, 'X-Total-Count'), 10)));
  }

  public getPayTags(cliRegId: number): Observable<PayTag[]> {
    return this.api.get(`/repository/api/v1/regulation/${cliRegId}/flatten/payTags`)
      .pipe(
        map(result => result.regulationFlatten?.payTags)
      );
  }
}
